/**
 * Prüfbefund als Checkliste (PV-M04, Wunsch von Mathias, 11/2026).
 *
 * Der Befund hatte bisher nur die Messwerte. Geprüft wird vor Ort aber
 * zuerst durch Besichtigen und Erproben — und genau das soll abzuhaken sein,
 * Punkt für Punkt, mit „in Ordnung“, „Mangel“ oder „entfällt“.
 *
 * Zwei Arten von Punkten:
 *
 *   Vorlage   — was der Prüfer ansieht. Die Liste steht in den Einstellungen
 *               und wird beim ersten Haken in den Befund kopiert: Wer die
 *               Vorlage später ändert, ändert keinen abgeschlossenen Befund.
 *   Planung   — was das Programm ohnehin rechnet: Stringspannung,
 *               Systemspannung, Spannungsfall, Verstringung. Das wird nicht
 *               abgefragt, sondern angezeigt; nur was fehlt, bleibt offen.
 */
/** Die Vorlage als Liste offener Punkte. */
export function punkteAusVorlage(vorlage) {
    return vorlage.abschnitte.flatMap((a) => a.punkte.map((text) => ({ abschnitt: a.titel, text })));
}
/**
 * Die Vorlage als Text zum Bearbeiten: eine Zeile je Abschnitt, darunter die
 * Punkte mit Strich. So lässt sie sich am Tablet ändern, ohne Tabelle.
 */
export function vorlageAlsText(vorlage) {
    return vorlage.abschnitte
        .map((a) => [a.titel, ...a.punkte.map((p) => `- ${p}`)].join('\n'))
        .join('\n\n');
}
/**
 * Text zurück in eine Vorlage. Zeilen mit Strich (oder Stern) sind Punkte,
 * alle anderen beginnen einen Abschnitt. Punkte vor dem ersten Abschnitt
 * landen unter „Allgemein“, leere Abschnitte fallen weg.
 */
export function vorlageAusText(text) {
    const abschnitte = [];
    for (const roh of text.split(/\r?\n/)) {
        const zeile = roh.trim();
        if (!zeile)
            continue;
        const punkt = /^[-*•–]\s*(.+)$/.exec(zeile);
        if (punkt) {
            if (abschnitte.length === 0)
                abschnitte.push({ titel: 'Allgemein', punkte: [] });
            abschnitte[abschnitte.length - 1].punkte.push(punkt[1].trim());
        }
        else {
            abschnitte.push({ titel: zeile, punkte: [] });
        }
    }
    return { abschnitte: abschnitte.filter((a) => a.punkte.length > 0) };
}
export function pruefstand(punkte) {
    const stand = { gesamt: punkte.length, io: 0, mangel: 0, entfaellt: 0, offen: 0 };
    for (const p of punkte) {
        if (p.ergebnis === 'io')
            stand.io++;
        else if (p.ergebnis === 'mangel')
            stand.mangel++;
        else if (p.ergebnis === 'entfaellt')
            stand.entfaellt++;
        else
            stand.offen++;
    }
    return stand;
}
/**
 * Passt das angekreuzte Gesamtergebnis zu den Punkten? „In Ordnung“ mit
 * einem Mangel darauf ist ein Widerspruch, den der Kunde unterschreibt.
 */
export function widerspruchImErgebnis(ergebnis, stand) {
    if (ergebnis === 'in_ordnung' && stand.mangel > 0) {
        return `„In Ordnung“ angekreuzt, aber ${stand.mangel} Punkt(e) mit Mangel.`;
    }
    if (ergebnis === 'in_ordnung' && stand.offen > 0) {
        return `„In Ordnung“ angekreuzt, aber ${stand.offen} Punkt(e) noch nicht geprüft.`;
    }
    if ((ergebnis === 'geringe_maengel' || ergebnis === 'nicht_in_ordnung') && stand.mangel === 0) {
        return 'Mängel angekreuzt, aber kein Punkt als Mangel vermerkt.';
    }
    return null;
}
/**
 * Was die Planung zum Befund beiträgt. Kein Punkt wird hier „in Ordnung“,
 * nur weil eine Angabe fehlt: Fehlt der Wechselrichter, ist die
 * Stringspannung offen, nicht bestanden.
 */
export function planungspunkte(e) {
    const warnungen = (arten) => e.befund.warnungen.filter((w) => arten.includes(w.art)).map((w) => w.text);
    const ausWarnungen = (text, arten, voraussetzung) => {
        if (voraussetzung)
            return { text, ergebnis: 'offen', grund: voraussetzung };
        const gefunden = warnungen(arten);
        return gefunden.length > 0 ? { text, ergebnis: 'mangel', grund: gefunden.join(' ') } : { text, ergebnis: 'io' };
    };
    // Ob es Strings gibt, sagen die verstringten Module — nicht die
    // Stringbefunde: Die rechnet die Anlagenprüfung nur mit Wechselrichter und
    // mit den elektrischen Daten des Moduls.
    const ohneStrings = e.befund.moduleVerstringt === 0;
    const elektrik = !e.wechselrichter
        ? 'Kein Wechselrichter eingetragen.'
        : ohneStrings
            ? 'Noch kein String mit Modulen.'
            : e.befund.befunde.size === 0
                ? 'Nicht gerechnet — die elektrischen Daten des Moduls fehlen.'
                : null;
    const punkte = [];
    punkte.push(e.befund.moduleGesamt === 0
        ? { text: 'Alle Module sind einem String zugeordnet', ergebnis: 'offen', grund: 'Noch keine Module belegt.' }
        : e.befund.ohneString.length > 0
            ? {
                text: 'Alle Module sind einem String zugeordnet',
                ergebnis: 'mangel',
                grund: `${e.befund.ohneString.length} von ${e.befund.moduleGesamt} Modulen ohne String.`,
            }
            : { text: 'Alle Module sind einem String zugeordnet', ergebnis: 'io' });
    punkte.push(e.befund.weggefallen.length > 0
        ? {
            text: 'Stringplan passt zur Belegung',
            ergebnis: 'mangel',
            grund: `${e.befund.weggefallen.length} Stringplatz/-plätze ohne Modul — die Belegung wurde nach dem Verstringen geändert.`,
        }
        : { text: 'Stringplan passt zur Belegung', ergebnis: ohneStrings ? 'offen' : 'io' });
    punkte.push(ausWarnungen('Leerlaufspannung bei Kälte unter der höchsten Eingangsspannung des Wechselrichters', ['string_zu_lang'], elektrik));
    punkte.push(ausWarnungen('MPP-Spannung im Sommer über der Mindestspannung des Wechselrichters', ['string_zu_kurz', 'unter_mindestspannung'], elektrik));
    punkte.push(ausWarnungen('Strom je MPP-Tracker und PV-Leistung innerhalb der Grenzen des Wechselrichters', ['strom_mppt', 'pv_leistung_zu_hoch'], elektrik));
    punkte.push(ausWarnungen('Leerlaufspannung bei Kälte unter der Systemspannung der Module', ['ueber_systemspannung'], elektrik ?? (e.systemspannungBekannt ? null : 'Systemspannung des Moduls nicht eingetragen.')));
    const zuViel = e.spannungsfaelle.filter((s) => !s.zulaessig);
    punkte.push(e.spannungsfaelle.length === 0
        ? {
            text: 'Spannungsfall DC höchstens 2 %, AC höchstens 3 %',
            ergebnis: 'offen',
            grund: 'Keine Leitungswege mit Länge eingetragen.',
        }
        : zuViel.length > 0
            ? {
                text: 'Spannungsfall DC höchstens 2 %, AC höchstens 3 %',
                ergebnis: 'mangel',
                grund: zuViel
                    .map((s) => `${s.bezeichnung}: ${s.prozent.toFixed(2).replace('.', ',')} % (Grenze ${s.grenzeProzent} %)`)
                    .join('; '),
            }
            : { text: 'Spannungsfall DC höchstens 2 %, AC höchstens 3 %', ergebnis: 'io' });
    return punkte;
}
//# sourceMappingURL=pruefbefund.js.map